import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import UploadImage from "../../UploadImage";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  buttonGroup: {
    textAlign: "right",
    marginTop: "-20px",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  customLabel: {
    margin: "0 !important",
    "& label": {
      color: "black",
      fontWeight: 700,
      fontSize: "16px",
    },
    "& p": {
      fontSize: "12px",
    },
  },
}));

const defaultFormValues = {
  createTime: dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
  displayDatetime: null,
  hkiffId: null,
  id: null,
  localizations: {
    en: {
      title: "",
      content: "",
    },
    tc: {
      title: "",
      content: "",
    },
  },
  sequence: null,
  status: {
    deleted: false,
    status: false,
  },
  image: "",
  updateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
  url: "",
  // to initialize during fetch and
  // to purge during submission
  date: dayjs().format("YYYY-MM-DD"),
  time: Date.now(),
};

function EDMEditPage({ disabled, ...props }) {
  const classes = useStyles();
  const { match, history } = props;
  const { id } = match.params;
  const [ paramId, setParamId ] = React.useState(id);
  const [defaultValue, setDefaultValue] = React.useState(defaultFormValues);
  const { setLoading } = useLoading();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
  });

  const onSubmit = async (value) => {
    setLoading(true);
    // purge date and time
    const { date, time, ...data } = value;
    const isValidDate = dayjs(date).isValid() && dayjs(time).isValid();
    const displayDatetime = isValidDate ? `${dayjs(date).format("YYYY-MM-DD")} ${dayjs(time).format("HH:mm:00")}` : dayjs().format("YYYY-MM-DD HH:mm:00");
    data.displayDatetime = displayDatetime;
    
    // post immediately if date and time are not set
    data.status.status = !isValidDate ? true : data.status.status;
    
    try {
      if (paramId !== "new") {
        const result = await apiHelper.hkiff.updateEDMById(paramId, data);
        if (result.status === 200) {
          toast.dark("EDM is updated.");
          getEDMById();
        }
      } else {
        const result = await apiHelper.hkiff.createEDM(data);
        if (result.status === 200) {
          setParamId(result.data.id);
          history.push(`/hkiff/edm/${result.data.id}`);
          toast.dark("EDM is created.");
        }
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const getEDMById = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getEDMById(id);
      // initialize date and time
      const data = result.data;
      const datetime = data.displayDatetime.split(" ");
      data.date = datetime[0];
      data.time = Date.parse(`${datetime[0]}T${datetime[1]}`);
      setDefaultValue(data);
      reset(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [id, reset, setLoading]);

  React.useEffect(() => {
    if (id !== "new") getEDMById();
  }, [getEDMById, id]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {`eDM > ${id === "new" ? "Add" : "Edit"} Post`}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Box className={classes.innerBox}>
            <Box className={classes.sectionBox}>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <strong>eDM Details</strong>
                </Grid>
              </Grid>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.title"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Title (English)"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.title"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Title (Traditional Chinese)"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="url"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="URL"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box className={classes.sectionBox}>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <strong>News Images</strong>
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12}>
                  {defaultValue && (
                    <UploadImage
                      name="image"
                      data={defaultValue}
                      control={control}
                      Controller={Controller}
                      setValue={setValue}
                      getValues={getValues}
                      disabled={disabled}
                      uploadLabel="Upload image"
                      changeLabel="Change image"
                      uploadFolder="ENEWSLETTER"
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box className={classes.innerBox}>
            <Box className={classes.sectionBox}>
              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <strong>Status Settings</strong>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={4}>
                    <Controller
                      control={control}
                      name="status.status"
                      render={({
                        field: { value, onChange, ...rest },
                        fieldState: { error },
                      }) => (
                        <ToggleButtonGroup
                          {...rest}
                          value={value}
                          onChange={(event, newFormats) => {
                            onChange(newFormats);
                          }}
                          exclusive
                          aria-label="Status"
                          className={classes.input}
                        >
                          <ToggleButton
                            value={true}
                            aria-label="Pending"
                            style={{ width: "50%" }}
                          >
                            Active
                          </ToggleButton>
                          <ToggleButton
                            value={false}
                            aria-label="Confirmed"
                            style={{ width: "50%" }}
                          >
                            Inactive
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <strong>Publish Settings</strong>
                </Grid>
                <Grid container item xs={6} direction="row">
                  <Grid item xs={6}>
                    <Controller
                      name={`date`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <KeyboardDatePicker
                          fullWidth
                          variant="inline"
                          format="dd/MM/yyyy"
                          margin="dense"
                          id="date-picker-inline"
                          disabled={disabled}
                          label="Date"
                          {...field}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          classes={{ root: classes.customLabel }}
                          KeyboardButtonProps={{
                            "aria-label": "change date",
                          }}
                          helperText={
                            "Your post will be posted immediately if you don’t set a custom date and time."
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name={`time`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <KeyboardTimePicker
                          fullWidth
                          variant="inline"
                          format="hh:mm a"
                          margin="dense"
                          disabled={disabled}
                          id="time-picker-inline"
                          label="Time"
                          {...field}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          classes={{ root: classes.customLabel }}
                          KeyboardButtonProps={{
                            "aria-label": "change time",
                          }}
                          helperText={""}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <div className={classes.buttonGroup}>
            {disabled || (
              <div className={classes.buttonGroup}>
                <Button type="button" onClick={() => {}}>
                  Preview
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {}}
                >
                  Save Draft
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#306F9D",
                    marginRight: "5px",
                    padding: "9px 16px",
                  }}
                >
                  Publish
                </Button>
                {match.params.id && (
                  <Button
                    variant="contained"
                    disableElevation
                    style={{
                      color: "white",
                      background: "#962013",
                      padding: "9px 16px",
                    }}
                    onClick={() => {}}
                  >
                    Delete
                  </Button>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </form>
    </Box>
  );
}

export default EDMEditPage;
